import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AhaUpsellModal from '../ui/AhaUpsellModal';
import { Award, Flag, CheckCircle, XCircle, ArrowLeft, BarChart2, FileText, Clock, AlertCircle, Trophy, Target, TrendingUp, Book, Headphones, MessageSquare, PenTool, Sparkles, ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { logExamCompletion } from '../../utils/activityLogger.js';
import { buildWritingDataFromExam } from '../../utils/writingDataFromExam.js';
import enhancedUserService from '../../services/enhancedUserService.js';
import { collection, doc, getDoc, getDocs, limit, onSnapshot, query } from 'firebase/firestore';
import { db, auth } from '../../firebase.js';
import examService from '../../services/examService.js';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';
import TeacherCorrections from './tasks/writing/TeacherCorrections.jsx';
import { sanitizeCorrectedText, SingleTaskScores, WritingFeedbackDisplay } from './writing/WritingFeedbackPanel.jsx';
import { useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import ExamAnalytics from '../exam/ExamAnalytics';
import ExplanationPanel from './ExplanationPanel';
import { isTableGapQuestion, formatTableGapAnswer, hasTableAnswer } from '../../utils/tableAnswers';
import { isVerdictQuestion, isMatchingQuestion, isAnswerCorrect } from '../../utils/answerCheck';
import { buildResultInsights } from '../../services/resultInsights';
import { ensureRetrySet } from '../../services/retryService';
import examAnalyticsService from '../../services/examAnalyticsService';
import ResultsStory from '../results/ResultsStory';
import { notesPartOf, notesPointsOf, scoreListeningNotes } from '../../services/notesScoringService.js';
// Helper function to get section icon
const getSectionIcon = (sectionId) => {
  const icons = {
    lesen: Book,
    hören: Headphones,
    schreiben: PenTool,
    sprechen: MessageSquare,
      grammar: FileText, // <-- ADD
    sprachbausteine: FileText // <-- ADDv
  };
  return icons[sectionId?.toLowerCase()] || Target;
};

// Helper function to get section color
const getSectionColor = (sectionId) => {
  const colors = {
    lesen: 'blue',
    hören: 'purple',
    schreiben: 'green',
    sprechen: 'orange'
  };
  return colors[sectionId?.toLowerCase()] || 'gray';
};

const getSectionDisplayName = (sectionId) => {
  const names = {
    lesen: 'Lesen',
    reading: 'Lesen',
    hören: 'Hören',
    listening: 'Hören',
    schreiben: 'Schreiben',
    writing: 'Schreiben',
    sprechen: 'Sprechen',
    speaking: 'Sprechen'
  };
  return names[sectionId?.toLowerCase()] || (sectionId ? String(sectionId).charAt(0).toUpperCase() + String(sectionId).slice(1).toLowerCase() : '');
};

const canonicalSectionId = (sectionId) => {
  const canonical = {
    lesen: 'lesen', reading: 'lesen',
    hören: 'hören', listening: 'hören',
    schreiben: 'schreiben', writing: 'schreiben',
    sprechen: 'sprechen', speaking: 'sprechen'
  };
  return canonical[sectionId?.toLowerCase()] || sectionId?.toLowerCase();
};

// Strip JSON/artifact junk, now in WritingFeedbackPanel.jsx

const AnimatedScoreCircle = ({ score, maxScore, passed, showAsPoints = false, isCorrecting = false, hasWriting = false }) => {
  const { t } = useTranslation();
  const [animatedScore, setAnimatedScore] = useState(0);
  const radius = 120;

  // Calculate percentage for circle animation
  const percentage = maxScore ? (score / maxScore) * 100 : score;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (animatedScore / 100) * circumference;

  useEffect(() => {
    if (isCorrecting) return; // Don't animate score if still correcting
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setAnimatedScore(prev => {
          const target = showAsPoints ? percentage : score;
          if (prev >= target) {
            clearInterval(interval);
            return target;
          }
          return prev + 1;
        });
      }, 20);
    }, 500);
    return () => clearTimeout(timer);
  }, [score, showAsPoints, percentage, isCorrecting]);

  // If writing is currently being corrected, mask the score completely
  if (isCorrecting && hasWriting) {
    return (
      <div className="relative flex flex-col items-center justify-center w-64 h-64 bg-gradient-to-br from-purple-50 to-blue-50 rounded-full border-[6px] border-[#3A86FF]/20 shadow-inner">
        <div className="absolute inset-0 rounded-full border-[6px] border-[#3A86FF] border-t-transparent animate-spin opacity-40"></div>
        <Sparkles className="w-10 h-10 text-[#3A86FF] animate-pulse mb-3" />
        <span className="text-xl font-bold text-center text-gray-800 px-4">
          {t('examResults.evaluatingTitle', 'Auswertung läuft')}
        </span>
        <span className="text-xs text-center text-gray-500 mt-2 px-6 leading-tight">
          {t('examResults.evaluatingDesc', 'Unser spezialisierter Korrektur-Assistent analysiert gerade Ihren Text...')}
        </span>
      </div>
    );
  }

  return (
    <div className="relative">
      <svg className="transform -rotate-90 w-64 h-64">
        <circle cx="128" cy="128" r={radius} stroke="currentColor" strokeWidth="8" fill="none" className="text-gray-100" />
        <circle
          cx="128"
          cy="128"
          r={radius}
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={`${passed ? 'text-[#3A86FF]' : 'text-black'} transition-all duration-1000 ease-out`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {showAsPoints ? (
          <>
            <span className="text-5xl font-bold text-black">{Math.round(score)}</span>
            <span className="text-2xl text-black/60">/ {maxScore}</span>
          </>
        ) : (
          <>
            <span className="text-6xl font-bold text-black">{animatedScore.toFixed(0)}%</span>
          </>
        )}
        <span className={`text-lg font-semibold mt-2 ${passed ? 'text-[#3A86FF]' : 'text-black/60'}`}>
          {passed ? t('examResults.passed') : t('examResults.notPassed')}
        </span>
      </div>
    </div>
  );
};

// Section Score Card Component
const SectionScoreCard = ({ sectionId, scoreData, onClick, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const Icon = getSectionIcon(sectionId);
  const percentage = scoreData.percentage || 0;

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      onClick={onClick}
      className={`transform transition-all duration-700 cursor-pointer hover:scale-105 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
    >
      <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 hover:border-[#3A86FF]/50 transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
              <Icon className="w-6 h-6 text-black" />
            </div>
            <h3 className="text-lg font-bold capitalize text-black">{sectionId}</h3>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-black">{percentage.toFixed(0)}%</p>
            <p className="text-sm text-black/60">{scoreData.score}/{scoreData.total}</p>
          </div>
        </div>

        <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-[#3A86FF] rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// Achievement Badge Component
const AchievementBadge = ({ title, description, icon: Icon, unlocked = false }) => {
  return (
    <div className={`relative p-4 rounded-xl border transition-all ${
      unlocked 
        ? 'bg-white border-[#3A86FF] shadow-sm' 
        : 'bg-gray-50 border-gray-200 opacity-50'
    }`}>
      <div className="flex items-center space-x-3">
        <div className={`p-2 rounded-lg ${unlocked ? 'bg-[#3A86FF]/10' : 'bg-gray-200'}`}>
          <Icon className={`w-5 h-5 ${unlocked ? 'text-[#3A86FF]' : 'text-gray-500'}`} />
        </div>
        <div>
          <h4 className={`font-semibold ${unlocked ? 'text-black' : 'text-gray-600'}`}>{title}</h4>
          <p className={`text-sm ${unlocked ? 'text-black/60' : 'text-gray-500'}`}>{description}</p>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Answer label resolution
//
// A stored answer is a raw id whose meaning depends on the task type: in a
// matching task "f" is simply option f, while on a telc richtig/falsch answer
// sheet "F" means "falsch". Resolving the id against the task's own option set
// FIRST, and only then falling back to richtig/falsch wording, is what keeps a
// matching answer from being printed as the word "Falsch".
// ---------------------------------------------------------------------------

// Where a part keeps the pool an answer id points at. Ordered most- to
// least-specific; `situations` is deliberately absent because those are the
// prompts, not the choices.
const ANSWER_POOL_KEYS = ['options', 'headings', 'headlines', 'advertisements', 'texts'];

const MAX_ANSWER_LABEL_LENGTH = 160;

const readOptionLabel = (option) => {
  const label = option?.text ?? option?.label ?? option?.title ?? option?.heading ?? option?.content;
  if (typeof label !== 'string') return null;
  const trimmed = label.trim();
  if (!trimmed) return null;
  return trimmed.length > MAX_ANSWER_LABEL_LENGTH
    ? `${trimmed.slice(0, MAX_ANSWER_LABEL_LENGTH).trimEnd()}…`
    : trimmed;
};

const findInPool = (pool, target) => {
  if (!Array.isArray(pool)) return null;
  const match = pool.find((option) => {
    if (!option || typeof option !== 'object') return false;
    const id = option.id ?? option.value ?? option.key;
    return id !== undefined && id !== null && String(id).toLowerCase() === target;
  });
  return match ? readOptionLabel(match) : null;
};

// Returns the human-readable choice an answer id refers to, or null when the id
// belongs to no pool we can see.
const findAnswerLabel = (raw, question, part) => {
  const target = raw.toLowerCase();

  // A question that carries its own options is self-contained: never fall back
  // to the part-level pools for it, or an id could be resolved against the
  // wrong list (e.g. a reading passage instead of an answer choice).
  if (Array.isArray(question?.options) && question.options.length > 0) {
    return findInPool(question.options, target);
  }

  for (const content of [question?.content, part?.content]) {
    if (!content || typeof content !== 'object') continue;
    for (const key of ANSWER_POOL_KEYS) {
      const label = findInPool(content[key], target);
      if (!label) continue;
      // On a matching answer sheet the learner marked a letter, so keep the
      // letter next to the text it stands for.
      return isMatchingQuestion(question, part) && /^[a-z]$/i.test(raw)
        ? `${raw.toUpperCase()} — ${label}`
        : label;
    }
  }
  return null;
};

const formatAnswerValue = (value, question, part, t) => {
  if (isTableGapQuestion(question)) {
    // Row answers are objects; the correct answer is stored as a readable summary.
    if (typeof value === 'string') return value;
    return hasTableAnswer(value) ? formatTableGapAnswer(question, value) : t('examResults.noAnswer');
  }
  if (value === null || value === undefined) return t('examResults.noAnswer');

  if (Array.isArray(value)) {
    const labels = value
      .map((entry) => formatAnswerValue(entry, question, part, t))
      .filter((label) => label && label !== t('examResults.noAnswer'));
    return labels.length ? labels.join(' / ') : t('examResults.noAnswer');
  }

  if (typeof value === 'boolean') {
    return value ? t('examResults.answerTrue') : t('examResults.answerFalse');
  }

  const raw = String(value).trim();
  if (!raw) return t('examResults.noAnswer');

  const label = findAnswerLabel(raw, question, part);
  if (label) return label;

  if (isVerdictQuestion(question, part)) {
    const lower = raw.toLowerCase();
    if (lower === 'true' || lower === 'r' || lower === 'richtig' || raw === '+') return t('examResults.answerTrue');
    if (lower === 'false' || lower === 'f' || lower === 'falsch' || raw === '-') return t('examResults.answerFalse');
    if (lower === 'ja' || lower === 'yes') return t('examResults.yes');
    if (lower === 'nein' || lower === 'no') return t('examResults.no');
    if (lower === 'x') return t('examResults.answerNotStated');
    return raw;
  }

  if (isMatchingQuestion(question, part)) {
    // "x" / "0" are the answer-sheet codes for "none of the texts fits".
    if (raw === 'x' || raw === 'X' || raw === '0') return t('examResults.answerNoMatch');
    // A bare option letter reads better capitalised on the answer sheet.
    if (/^[a-z]$/.test(raw)) return raw.toUpperCase();
  }

  return raw;
};

// Question Result Card Component
const QuestionResult = ({ question, part, flaggedQuestions }) => {
  const { t } = useTranslation();
  const isFlagged = flaggedQuestions.includes(question.questionId);
  // Cloze items number their blanks with `blankNumber` rather than `questionNumber`.
  const displayNumber = question.questionNumber ?? question.blankNumber ?? null;
  const displayText = question.questionText || question.statement || question.situationText || '';
  const getOptionText = (optionId) => formatAnswerValue(optionId, question, part, t);

  return (
    <div className={`relative p-4 rounded-xl transition-all hover:shadow-md ${
      question.isCorrect 
        ? 'bg-green-50 border border-green-200' 
        : 'bg-red-50 border border-red-200'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
              question.isCorrect ? 'bg-green-200 text-green-700' : 'bg-red-200 text-red-700'
            }`}>
              {displayNumber ?? '–'}
            </span>
            {displayText && <p className="font-medium text-gray-800 flex-1">{displayText}</p>}
          </div>

          <div className="ml-10 space-y-1 text-sm">
            <p className="text-gray-600">
              {t('examResults.yourAnswer')} <span className={`font-semibold ${question.isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                {getOptionText(question.userAnswer) || t('examResults.noAnswer')}
              </span>
            </p>
            {!question.isCorrect && (
              <p className="text-gray-600">
                {t('examResults.correctAnswer')} <span className="font-semibold text-green-700">
                  {getOptionText(question.correctAnswer)}
                </span>
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center ml-4 space-x-2">
          {isFlagged && <Flag className="w-5 h-5 text-yellow-500" />}
          {question.isCorrect ? (
            <CheckCircle className="w-6 h-6 text-green-600" />
          ) : (
            <XCircle className="w-6 h-6 text-red-600" />
          )}
        </div>
      </div>
      {question.explanation && (
        <div className="ml-10">
          <ExplanationPanel explanation={question.explanation} />
        </div>
      )}
    </div>
  );
};

// Section Detail Tab Content - ENHANCED: Handle writing sections and empty sections
const SectionDetailContent = ({ sectionData, results, writingFeedback, writingData, isCorrecting, correctionError, onRequestCorrection }) => {
  const { t } = useTranslation();
  const isWritingSection = sectionData?.sectionId?.toLowerCase() === 'schreiben' ||
                          sectionData?.sectionId?.toLowerCase().includes('writing');
  const isSpeakingSection = sectionData?.sectionId?.toLowerCase() === 'sprechen' ||
                           sectionData?.sectionId?.toLowerCase().includes('speaking');

  return (
    <div className="p-4 md:p-8 space-y-4 md:space-y-6">
      {/* Always show writing feedback in Schreiben section */}
      {isWritingSection && (
        <div className="mb-8">
          {/* Show writing content even if no traditional sectionData */}
          {isCorrecting && (
            <div className="flex items-center space-x-4 text-purple-700 bg-purple-50 p-6 rounded-xl border border-purple-100 shadow-sm">
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 border-4 border-purple-200 rounded-full" />
                <div className="absolute inset-0 w-10 h-10 border-4 border-purple-600 rounded-full border-t-transparent animate-spin" />
              </div>
              <div>
                <span className="font-bold block text-lg">
                  {t('examResults.specializedEngine', 'Korrektur in Bearbeitung...')}
                </span>
                <span className="text-sm text-purple-600">
                  {t('examResults.specializedEngineDesc', 'Unser zertifizierter Korrektur-Algorithmus bewertet Wortschatz, Grammatik und Kohärenz.')}
                </span>
              </div>
            </div>
          )}
          {correctionError && (
            <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-4 rounded-xl">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{t('examResults.correctionFailed')}</span>
            </div>
          )}
          {writingFeedback && writingData && (
            <WritingFeedbackDisplay
              feedback={writingFeedback}
              writingData={writingData}
            />
          )}
          {/* Safety: show user's submitted text even when no correction (so text is never lost) */}
          {writingData && !writingFeedback && !isCorrecting && !correctionError && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-200 p-6 space-y-6">
              <h5 className="text-lg font-bold text-gray-800">{t('examResults.yourSubmittedText')}</h5>
              <p className="text-sm text-gray-600 mb-4">{t('examResults.textsSavedNoCorrection')}</p>
              {Object.entries(writingData.texts || {}).map(([key, taskData]) => (
                <div key={key} className="space-y-2">
                  {writingData.hasMultipleTexts && taskData.partName && (
                    <p className="text-sm font-semibold text-purple-800">{taskData.partName}</p>
                  )}
                  <div className="p-4 bg-white border border-gray-200 rounded-xl">
                    <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">{taskData.text}</p>
                  </div>
                </div>
              ))}
              {typeof onRequestCorrection === 'function' && (
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={onRequestCorrection}
                    className="inline-flex items-center justify-center min-h-[48px] px-6 py-3 rounded-xl font-semibold text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors touch-manipulation"
                  >
                    <Sparkles className="w-5 h-5 mr-2" aria-hidden />
                    {t('examResults.requestCorrection')}
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Show message if no writing tasks were found */}
          {!writingData && !isCorrecting && !correctionError && (
            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <PenTool className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">{t('examResults.noWritingTasks')}</p>
            </div>
          )}
        </div>
      )}

      {/* Speaking Section Placeholder */}
      {isSpeakingSection && (!sectionData?.hasContent || !sectionData?.parts?.length) && (
        <div className="text-center p-8 bg-gray-50 rounded-xl">
          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('examResults.speakingNotGraded')}</p>
          <p className="text-sm text-gray-500 mt-2">{t('examResults.speakingRealExamNote')}</p>
        </div>
      )}

      {/* Question Review for sections with questions */}
      {sectionData?.parts && sectionData.parts.length > 0 && (
        <div>
          <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
            {React.createElement(getSectionIcon(sectionData.sectionId), { className: "w-6 h-6 mr-2" })}
            {t('examResults.questionDetails')}
          </h4>
          <div className="space-y-6">
            {sectionData.parts
              .filter(part => part.questions && part.questions.length > 0)
              .map((part, partIndex) => (
                <div key={part.partId} className="animate-fade-in" style={{ animationDelay: `${partIndex * 100}ms` }}>
                  <h5 className="font-semibold text-gray-700 mb-3 flex items-center">
                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2" />
                    {part.partName}
                  </h5>
                  <div className="space-y-3">
                    {part.questions
                      .filter(q => !q.isExample)
                      .map(q => {
                        const userAnswer = results?.userAnswers?.[part.partId]?.[q.questionId];
                        const questionWithAnswer = {
                          ...q,
                          userAnswer,
                          isCorrect: isAnswerCorrect(userAnswer, q, part)
                        };

                        return (
                          <QuestionResult
                            key={q.questionId}
                            question={questionWithAnswer}
                            part={part}
                            flaggedQuestions={results?.flaggedQuestions || []}
                          />
                        );
                      })}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Show message for sections without content */}
      {(!sectionData?.hasContent || !sectionData?.parts?.length) && !isWritingSection && !isSpeakingSection && (
        <div className="text-center p-8 bg-gray-50 rounded-xl">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('examResults.noTasksForSection')}</p>
        </div>
      )}
    </div>
  );
};

// Main Results Page Component
const ExamResultsPage = ({ examData, results, onBack, subscriptionStatus }) => {
  const { t } = useTranslation();
  const [writingFeedback, setWritingFeedback] = useState(null);
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [correctionError, setCorrectionError] = useState(null);
  const [writingData, setWritingData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const location = useLocation();
  // When a saved attempt was taken on a different exam variant than the one
  // currently loaded, the matching variant JSON is loaded into this override so
  // question details line up with the attempt's answers (see effect below).
  const [variantExamData, setVariantExamData] = useState(null);
  const finalExamData = variantExamData || location.state?.examData || examData;

  const [finalResults, setFinalResults] = useState(location.state?.results || results);
  console.log('🔍 DEBUG maxScore on page load:', finalResults?.maxScore, 'provider:', finalResults?.provider);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useLocalizedNavigate();
  const [showAhaModal, setShowAhaModal] = useState(false);
  const [pendingExitNav, setPendingExitNav] = useState(null);
  const hasAutoShownFirstExamAhaRef = useRef(false);

  const isFreeTierUser = useMemo(() => {
    if (!subscriptionStatus) return false;
    if (subscriptionStatus.isFree === true) return true;
    if (subscriptionStatus.isActive === true) return false;

    const normalizedStatus = String(subscriptionStatus.status || '').toLowerCase();
    return ['free', 'expired', 'inactive', 'cancelled', 'canceled'].includes(normalizedStatus);
  }, [subscriptionStatus]);

  // Show upsell only for free users after finishing exam and attempting to exit this screen.
  const maybeShowExamAha = (navigateFn) => {
    if (isFreeTierUser) {
      setPendingExitNav(() => navigateFn);
      setShowAhaModal(true);
      return;
    }
    navigateFn();
  };

  const handleExit = (destination = 'back') => {
    if (destination === 'dashboard') {
      maybeShowExamAha(() => navigate('/dashboard'));
      return;
    }
    if (destination === 'exam') {
      maybeShowExamAha(() => navigate('/exam'));
      return;
    }
    if (destination === 'lessons') {
      maybeShowExamAha(() => navigate('/lessons'));
      return;
    }

    maybeShowExamAha(() => onBack?.());
  };

  const handleAhaDismiss = () => {
    setShowAhaModal(false);
    if (typeof pendingExitNav === 'function') {
      pendingExitNav();
    }
    setPendingExitNav(null);
  };

  const handleAhaUpgrade = () => {
    setShowAhaModal(false);
    setPendingExitNav(null);
  };

  // True when viewing a result that was already saved (from history or loaded from Firebase). Do NOT treat as "saved" when we just finished and only have id from the save – we still need to submit writing (no writingFeedback/writingSubmissionId yet).
  const isViewingSavedResult = !!(
    finalResults?.id &&
    (finalResults.writingFeedback != null || finalResults.writingSubmissionId != null || (location.state?.results && location.state.results?.id))
  );

  // Saved attempts store which variant they were taken on (examFileName, older
  // results only variantName). Answers are keyed to that variant's questions, so
  // when it differs from the currently loaded exam JSON, load the right variant
  // before rendering question details.
  useEffect(() => {
    if (!isViewingSavedResult || !finalResults) return;
    const provider = finalResults.provider || finalExamData?.examProvider;
    const level = finalResults.level || finalExamData?.level;
    const wantFile = finalResults.examFileName || null;
    const wantName = finalResults.variantName || null;
    if (!provider || !level || (!wantFile && !wantName)) return;
    if (finalExamData && (wantFile ? finalExamData.examFileName === wantFile : finalExamData.variantName === wantName)) return;
    const variants = examService.getAvailableExams()[provider.toLowerCase()]?.[level.toUpperCase()] || [];
    // Old results carry the pre-rename display name — match legacyName too
    const config = variants.find(v => v.file && (wantFile ? v.file === wantFile : (v.name === wantName || v.legacyName === wantName)));
    if (!config || finalExamData?.examFileName === config.file) return;
    let cancelled = false;
    examService.loadExam(provider, level, config.id)
      .then((exam) => { if (!cancelled) setVariantExamData(exam); })
      .catch((err) => console.warn('Could not load variant exam for saved attempt:', err));
    return () => { cancelled = true; };
  }, [isViewingSavedResult, finalResults, finalExamData]);

  // Show the Aha upsell automatically after every exam completion for free-tier users.
  useEffect(() => {
    if (isViewingSavedResult || hasAutoShownFirstExamAhaRef.current) return;
    if (!subscriptionStatus) return;

    hasAutoShownFirstExamAhaRef.current = true;
    if (isFreeTierUser) {
      setPendingExitNav(null);
      setShowAhaModal(true);
    }
  }, [isViewingSavedResult, subscriptionStatus, isFreeTierUser]);

  const hasSavedRef = useRef(false);
  const examResultIdRef = useRef(null);
  const hasProcessedResultsRef = useRef(false);
  /** In-flight save promise so re-runs of the effect can await the same save and get examResultId. Do NOT clear in cleanup to avoid duplicate doc when effect re-runs. */
  const saveExamResultPromiseRef = useRef(null);
  /** Identifies the attempt we saved (or are saving). When this changes (e.g. user opened a different result), we clear the promise ref so a new save is allowed. */
  const lastSaveAttemptKeyRef = useRef(null);

  // Tab title: e.g. "TELC B1 Results | Logami" based on exam provider and level
  useEffect(() => {
    const provider = (finalExamData?.examProvider || finalResults?.provider || '').toString().trim();
    const level = (finalExamData?.level || finalResults?.level || '').toString().trim().toUpperCase();
    if (!provider || !level) return;
    const provKey = provider.toLowerCase();
    const providerLabel = provKey === 'telc' ? 'TELC' : provKey === 'goethe' ? 'Goethe' : (provKey === 'osd' || provKey === 'ösd') ? 'ÖSD' : provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
    const prev = document.title;
    document.title = `${providerLabel} ${level} ${t('examResults.tabTitleSuffix', { defaultValue: 'Results' })} | Logami`;
    return () => { document.title = prev; };
  }, [finalExamData?.examProvider, finalExamData?.level, finalResults?.provider, finalResults?.level, t]);

  // telc C1 Hören Teil 3 (65–74): the written notes are marked once by the AI
  // rater. Its points are kept with the answers (userAnswers._notesPoints),
  // where every score calculation on this page reads them.
  const notesRequestedRef = useRef(null);
  useEffect(() => {
    const resultId = finalResults?.id;
    const answers = finalResults?.userAnswers;
    const uid = auth.currentUser?.uid;
    if (!resultId || !answers || answers._notesPoints || !uid || !finalExamData) return;
    if (String(finalExamData.level).toUpperCase() !== 'C1' || !notesPartOf(finalExamData)) return;
    if (notesRequestedRef.current === resultId) return;
    notesRequestedRef.current = resultId;
    scoreListeningNotes(finalExamData, answers).then(async ({ status, scores }) => {
      if (status !== 'done' && status !== 'empty') return;
      const userAnswers = { ...answers, _notesPoints: notesPointsOf(scores) };
      const feedback = finalResults.writingFeedback;
      const aiWriting = feedback?.scores?.overallScore ?? feedback?.scores?.scaledScore ?? feedback?.overallScore;
      const writingPercent = aiWriting != null ? (Number(aiWriting) / (feedback?.guidelineMetadata?.maxPoints || 48)) * 100 : null;
      const updated = examService.calculateFinalScore(finalExamData, userAnswers, writingPercent, { writingFeedback: feedback });
      setFinalResults((prev) => ({ ...prev, ...updated, userAnswers }));
      await enhancedUserService.updateExamResultWithFeedback(uid, resultId, { ...updated, userAnswers, listeningNotesScores: scores || {} });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalResults?.id, finalResults?.userAnswers, finalExamData]);

  // Run correction when user clicks "Korrektur anfordern" (saved result with text but no feedback)
  const handleRequestCorrection = useCallback(async () => {
    const userId = auth.currentUser?.uid;
    if (!userId || !writingData || !finalExamData || !finalResults?.id) {
      setCorrectionError(t('examResults.correctionStartFailed'));
      return;
    }
    const resultId = finalResults.id;
    const examData = finalExamData;
    const userAnswers = finalResults.userAnswers || {};
    const properWritingData = { hasMultipleTexts: writingData.hasMultipleTexts, texts: writingData.texts, parts: writingData.parts || [] };

    setIsCorrecting(true);
    setCorrectionError(null);

    try {
      const submissionData = {
        examProvider: examData.examProvider,
        examLevel: examData.level,
        hasMultipleTexts: properWritingData.hasMultipleTexts,
        texts: properWritingData.texts,
        userText: Object.values(properWritingData.texts || {})[0]?.text || '',
        taskType: 'writing',
        examResultId: resultId || null
      };

      const submissionId = await enhancedUserService.saveWritingSubmission(userId, submissionData);
      if (!submissionId) {
        setCorrectionError(t('examResults.correctionSubmitError'));
        setIsCorrecting(false);
        return;
      }

      await enhancedUserService.updateExamResultWithFeedback(userId, resultId, {
        writingSubmissionId: submissionId,
        writingData: properWritingData
      });

      const submissionRef = doc(db, 'writingSubmissions', submissionId);
      const unsubscribe = onSnapshot(submissionRef, async (docSnapshot) => {
        if (!docSnapshot.exists() || docSnapshot.data()?.status !== 'corrected') return;

        const data = docSnapshot.data();
        setWritingFeedback(data);
        setIsCorrecting(false);

        const aiWritingScore = data.scores?.overallScore ?? data.scores?.scaledScore ?? data.overallScore;
        let payload = { writingFeedback: data, writingData: properWritingData };

        if (aiWritingScore != null && resultId) {
          const provider = examData.examProvider?.toUpperCase();
          const level = examData.level?.toUpperCase();
          const writingScoreForCalc = provider === 'GOETHE'
            ? aiWritingScore
            : (Number(aiWritingScore) / (data.guidelineMetadata?.maxPoints || (level === 'C1' ? 48 : (provider === 'OSD' ? 30 : 45)))) * 100;
          const updatedScore = examService.calculateFinalScore(examData, userAnswers, writingScoreForCalc, { writingFeedback: data });
          payload = { ...payload, ...updatedScore };
          setFinalResults(prev => ({ ...prev, ...updatedScore, writingFeedback: data }));
        }

        await enhancedUserService.updateExamResultWithFeedback(userId, resultId, payload);
        unsubscribe();
      }, (err) => {
        console.error('Error listening to submission:', err);
        setCorrectionError(t('examResults.correctionFetchError'));
        setIsCorrecting(false);
      });
    } catch (err) {
      console.error('Request correction error:', err);
      setCorrectionError(t('examResults.correctionSubmitError'));
      setIsCorrecting(false);
    }
  }, [writingData, finalExamData, finalResults?.id, finalResults?.userAnswers]);

  // Live listener: when viewing a saved result, keep in sync with Firestore (e.g. when Cloud Function syncs writingFeedback)
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    const resultId = finalResults?.id;
    if (!uid || !resultId || !isViewingSavedResult) return;

    const resultRef = doc(db, 'users', uid, 'examResults', resultId);
    const unsubscribe = onSnapshot(
      resultRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        const merged = { id: snap.id, ...data };
        setFinalResults((prev) => ({ ...prev, ...merged }));
        if (data.writingFeedback) {
          setWritingFeedback(data.writingFeedback);
          if (data.writingData) setWritingData(data.writingData);
          setCorrectionError(null);
          setIsCorrecting(false);
        }
        if (data.status?.startsWith?.('error_')) {
          setCorrectionError(t('examResults.correctionFailed'));
          setIsCorrecting(false);
        }
      },
      (err) => console.warn('Exam result live listener error:', err)
    );
    return () => unsubscribe();
  }, [isViewingSavedResult, finalResults?.id, auth.currentUser?.uid, t]);

  useEffect(() => {
  // Skip recalculation when viewing a saved result: data is already persisted. Recalculating here causes save → listener → state update → effect re-run → infinite loop.
  if (isViewingSavedResult) return;

  const writingScore = writingFeedback?.scores?.overallScore || writingFeedback?.overallScore;
if (writingScore && finalExamData && finalResults) {
    console.log('🔄 ===== RECALCULATION TRIGGERED =====');

    // ✅ Declare variables at the TOP
    const aiWritingScore = writingScore;
    const provider = finalExamData.examProvider?.toUpperCase();
    const level = finalExamData.level?.toUpperCase();

    console.log('📝 Input data:', {
      aiWritingScore,
      currentOverallScore: finalResults.overallScore,
      currentMaxScore: finalResults.maxScore,
      provider,
      level,
      isViewingSavedResult
    });
    // Only recalculate for NEW exams (just completed); saved results are skipped above.
    console.log('✅ New exam - proceeding with recalculation');

    let updatedResults;
    const isC1 = level === 'C1';
    const maxWritingPoints = isC1 ? 48 : 45;

    if (provider === 'TELC' || provider === 'OSD') {
  // For OSD, determine the correct max based on level
  const osdWritingMax = (provider === 'OSD' && level !== 'B1') ? 30 : maxWritingPoints;
  const correctMaxForConversion = provider === 'OSD' ? osdWritingMax : maxWritingPoints;

  const writingScorePercentage = (aiWritingScore / correctMaxForConversion) * 100;

      console.log('📝 Writing score conversion:', {
        aiPoints: aiWritingScore,
        maxPoints: maxWritingPoints,
        percentage: writingScorePercentage
      });

      // ALWAYS call calculateFinalScore - it will include writing
updatedResults = examService.calculateFinalScore(
    finalExamData,
    finalResults.userAnswers,
    writingScorePercentage,
    { writingFeedback }
);

// 🔍 ADD THESE 6 LINES:
console.log('🐛 DEBUG calculateFinalScore returned:', {
    provider: updatedResults.provider,
    overallScore: updatedResults.overallScore,
    maxScore: updatedResults.maxScore,
    level: finalExamData.level
});

    } else if (provider === 'GOETHE') {
      const writingScorePercentage = aiWritingScore;
      updatedResults = examService.calculateFinalScore(
        finalExamData,
        finalResults.userAnswers,
        writingScorePercentage
      );
    }

    console.log('📊 ===== RECALCULATION RESULTS =====');
    console.log('Before:', {
      overallScore: finalResults.overallScore,
      maxScore: finalResults.maxScore
    });
    console.log('After:', {
      overallScore: updatedResults.overallScore,
      maxScore: updatedResults.maxScore,
      writtenScore: updatedResults.writtenScore
    });

    // Merge with existing results
    const mergedResults = {
      ...finalResults,
      ...updatedResults,
      writingFeedback,
      writingData
    };

    // Update state
    setFinalResults(mergedResults);

    if (finalResults.id && auth.currentUser) {
  console.log('💾 Saving recalculated results to database...');
  console.log('📦 Data being sent to database:', {
    userId: auth.currentUser.uid,
    examResultId: finalResults.id,
    dataToUpdate: {
      overallScore: updatedResults.overallScore,
      maxScore: updatedResults.maxScore,
      percentage: updatedResults.percentage,
      sectionScores: updatedResults.sectionScores,
      writtenScore: updatedResults.writtenScore,
      oralScore: updatedResults.oralScore,
      passed: updatedResults.passed
    }
  });

  enhancedUserService.updateExamResultWithFeedback(
    auth.currentUser.uid,
    finalResults.id,
    {
      overallScore: updatedResults.overallScore,
      maxScore: updatedResults.maxScore,
      percentage: updatedResults.percentage,
      sectionScores: updatedResults.sectionScores,
      writtenScore: updatedResults.writtenScore,
      oralScore: updatedResults.oralScore,
      passed: updatedResults.passed,
      writingFeedback: writingFeedback,
      writingData: writingData
    }
  ).then((result) => {
    console.log('✅ SAVED SUCCESSFULLY');
    console.log('💾 Save operation returned:', result);
  }).catch(error => {
    console.error('❌ Error saving:', error);
  });
}

    console.log('✅ UPDATE COMPLETE');
  }
}, [writingFeedback, finalExamData, isViewingSavedResult]);
  const examSections = useMemo(() => {
    if (!finalExamData || !finalResults) return [];

    // Map section names to core skills to avoid duplicates
    const sectionNameMapping = {
      'leseverstehen': 'lesen',
      'hörverstehen': 'hören',
      'schriftlicher ausdruck': 'schreiben',
      'mündlicher ausdruck': 'sprechen',
      'reading': 'lesen',
      'listening': 'hören',
      'writing': 'schreiben',
      'speaking': 'sprechen',
        'grammar': 'sprachbausteine'
    };

    const coreSkillSections = {};

    // Process existing exam sections and map them to core skills
    (finalExamData?.sections || []).forEach(section => {
      const normalizedName = section.sectionId.toLowerCase();
      const coreSkill = sectionNameMapping[normalizedName] ||
                       sectionNameMapping[section.sectionName?.toLowerCase()] ||
                       normalizedName;

      // Include parts that have either questions OR are writing/speaking tasks
      const relevantParts = section.parts.filter(part => {
        const hasQuestions = part.questions && part.questions.length > 0;
        const isWritingTask = ['writing', 'choice-writing-essay', 'email', 'email-formal', 'email-informal',
          'forum-post', 'formal-response-email', 'formal-complaint', 'formal-message',
          'formal-letter', 'informal-letter', 'essay', 'argumentative-essay'].includes(part.taskType);
        const isSpeakingTask = ['conversation', 'presentation', 'discussion', 'planning',
          'conversation-introductory', 'discussion-feedback', 'presentation-with-summary'].includes(part.taskType);

        return hasQuestions || isWritingTask || isSpeakingTask;
      });

      // Merge into core skill section or create new one
      if (!coreSkillSections[coreSkill]) {
        coreSkillSections[coreSkill] = {
          sectionId: coreSkill,
          sectionName: coreSkill.charAt(0).toUpperCase() + coreSkill.slice(1),
          originalSectionId: section.sectionId,
          parts: relevantParts,
          hasContent: relevantParts.length > 0
        };
      } else {
        // Merge parts if multiple sections map to same core skill
        coreSkillSections[coreSkill].parts = [
          ...coreSkillSections[coreSkill].parts,
          ...relevantParts
        ];
        coreSkillSections[coreSkill].hasContent = coreSkillSections[coreSkill].hasContent || relevantParts.length > 0;
      }
    });

    // Ensure all four core skills exist (add empty placeholders if missing)
    const coreSkills = ['lesen', 'hören', 'schreiben', 'sprechen'];
    coreSkills.forEach(skillId => {
      if (!coreSkillSections[skillId]) {
        coreSkillSections[skillId] = {
          sectionId: skillId,
          sectionName: skillId.charAt(0).toUpperCase() + skillId.slice(1),
          parts: [],
          hasContent: false
        };
      }
    });

    return Object.values(coreSkillSections);
  }, [finalExamData, finalResults]);

  // ---- Corrected-exam view (ResultsStory): only for papers that carry smart tags.
  // Everything above (saving, writing correction, live sync) stays exactly as it is.
  const hasSmartTags = useMemo(() => (finalExamData?.sections || []).some((s) =>
    (s.parts || []).some((p) => (p.questions || []).some((q) => q.smartTags))), [finalExamData]);
  const [resultTaxonomy, setResultTaxonomy] = useState(null);
  useEffect(() => {
    if (!hasSmartTags || resultTaxonomy) return undefined;
    let cancelled = false;
    fetch('/data/result-tags/taxonomy.json')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((json) => { if (!cancelled) setResultTaxonomy(json); })
      .catch((err) => console.warn('Result taxonomy not loaded, using the classic view:', err));
    return () => { cancelled = true; };
  }, [hasSmartTags, resultTaxonomy]);
  const resultInsights = useMemo(() => {
    if (!hasSmartTags || !resultTaxonomy || !finalResults?.userAnswers) return null;
    try {
      return buildResultInsights({
        exam: finalExamData,
        userAnswers: finalResults.userAnswers,
        flagged: finalResults.flaggedQuestions || [],
        hasFlags: finalResults.examMode !== 'paper',
        taxonomy: resultTaxonomy,
        writingCorrections: writingFeedback?.corrections || writingFeedback?.feedback?.corrections || [],
      });
    } catch (err) {
      console.error('Result insights failed, using the classic view:', err);
      return null;
    }
  }, [hasSmartTags, resultTaxonomy, finalExamData, finalResults?.userAnswers, finalResults?.flaggedQuestions, finalResults?.examMode, writingFeedback]);
  // The previous attempt at the same exam and level, for "since your last attempt".
  const [previousAttempt, setPreviousAttempt] = useState(null);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!resultInsights || !uid || !finalExamData?.examProvider) return undefined;
    let cancelled = false;
    examAnalyticsService.getExamHistory(uid, finalExamData.examProvider, finalExamData.level, 6)
      .then((history) => {
        const mine = finalResults?.completedAt ? new Date(finalResults.completedAt?.toDate?.() || finalResults.completedAt).getTime() : Date.now();
        const earlier = (history || []).filter((h) => h.id !== finalResults?.id && h.sectionScores
          && new Date(h.completedAt?.toDate?.() || h.completedAt).getTime() < mine);
        if (!cancelled) setPreviousAttempt(earlier[earlier.length - 1] || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(resultInsights), finalResults?.id, finalExamData?.examProvider, finalExamData?.level]);

  // "Retry my mistakes": one set per fresh attempt. Only READS the save promise/ref
  // above to learn the saved id; the save logic itself is untouched.
  const [retrySet, setRetrySet] = useState(null);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!resultInsights || !uid) return undefined;
    let cancelled = false;
    const run = async (tries) => {
      const id = finalResults?.id || examResultIdRef.current || (await saveExamResultPromiseRef.current);
      if (cancelled) return;
      if (!id) { if (tries > 0) setTimeout(() => run(tries - 1), 3000); return; }
      const set = await ensureRetrySet(uid, {
        examResultId: id, exam: finalExamData, review: resultInsights.review,
        completedAt: finalResults?.completedAt?.toDate?.() || finalResults?.completedAt,
      });
      if (!cancelled) setRetrySet(set);
    };
    run(5).catch((err) => console.warn('Retry set not saved:', err));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(resultInsights), finalResults?.id]);

  const tabs = useMemo(() => [
    { id: 'overview', name: t('examResults.tabOverview'), icon: BarChart2 },
    { id: 'analytics', name: t('examResults.tabProgress'), icon: TrendingUp },
    ...examSections.map(section => ({
      id: section.sectionId,
      name: section.sectionName,
      icon: getSectionIcon(section.sectionId)
    }))
  ], [examSections, t]);

  const MOBILE_VISIBLE_TABS = 2;
  const [mobileTabOffset, setMobileTabOffset] = useState(0);
  const maxMobileOffset = Math.max(0, tabs.length - MOBILE_VISIBLE_TABS);
  const visibleTabs = tabs.slice(mobileTabOffset, mobileTabOffset + MOBILE_VISIBLE_TABS);
  useEffect(() => {
    const idx = tabs.findIndex((tab) => tab.id === activeTab);
    if (idx < 0) return;
    const maxO = Math.max(0, tabs.length - MOBILE_VISIBLE_TABS);
    const want = Math.max(0, Math.min(idx, maxO));
    setMobileTabOffset((prev) => (prev !== want ? want : prev));
  }, [activeTab, tabs.length]);

  // Writing processing effect (unchanged from original)
 useEffect(() => {
    let isMounted = true;
    let hasProcessed = false;
    let unsubscribeWriting = null;

    const processResults = async () => {
      if (hasProcessed || !isMounted) return;
      if (hasProcessedResultsRef.current) return;
      hasProcessed = true;
      hasProcessedResultsRef.current = true;
        if (finalResults?.id && !isViewingSavedResult) {
          console.log('⏭️ Exam already saved with ID:', finalResults.id);
          if (isMounted) setIsLoading(false);
          return;
      }
      // When viewing a saved result, don't show loading again on re-runs (e.g. from Firestore listener); data is already there.
      if (!isViewingSavedResult) setIsLoading(true);

      // If no results at all, show error
      if (!finalResults) {
        console.error('No results data available');
        if (isMounted) setIsLoading(false);
        return;
      }

      // If viewing saved results, check for saved writing feedback
if (isViewingSavedResult) {
  console.log('📂 ===== LOADING SAVED EXAM RESULT =====');
  console.log('📊 Saved exam data:', {
    examId: finalResults.id,
    provider: finalResults.provider,
    overallScore: finalResults.overallScore,
    maxScore: finalResults.maxScore,
    hasWritingFeedback: !!finalResults.writingFeedback,
    writingScore: finalResults.writingFeedback?.scores?.overallScore,
    scaledScore: finalResults.writingFeedback?.scores?.scaledScore,
    writtenScore: finalResults.writtenScore,
    sectionScores: finalResults.sectionScores
  });

  if (isMounted) {
    if (finalResults.writingFeedback) {
     // Check both nested (TELC/Goethe) and flat (OSD) structures
const savedOverallScore = finalResults.writingFeedback.overallScore || finalResults.writingFeedback.scores?.overallScore;
const savedScaledScore = finalResults.writingFeedback.scaledScore || finalResults.writingFeedback.scores?.scaledScore;
const savedRawScore = finalResults.writingFeedback.rawScore || finalResults.writingFeedback.scores?.rawScore;

console.log('✅ Found saved writing feedback:', {
  overallScore: savedOverallScore,
  scaledScore: savedScaledScore,
  rawScore: savedRawScore,
  status: finalResults.writingFeedback.status
});
      setWritingFeedback(finalResults.writingFeedback);
      setWritingData(finalResults.writingData);
    } else {
      // Safety: derive writingData from userAnswers so user's text is always displayed even without correction
      const derived = buildWritingDataFromExam(finalExamData, finalResults.userAnswers || {});
      if (derived) {
        setWritingData(derived);
      } else {
        setWritingData(finalResults.writingData || null);
      }
      // Re-fetch exam result so we get writingFeedback if cloud synced it after user left the page
      if (finalResults.id && auth.currentUser && isMounted) {
        getDoc(doc(db, 'users', auth.currentUser.uid, 'examResults', finalResults.id))
          .then((snap) => {
            if (!isMounted || !snap.exists()) return;
            const data = snap.data();
            if (data.writingFeedback?.status === 'corrected') {
              setWritingFeedback(data.writingFeedback);
              if (data.writingData) setWritingData(data.writingData);
              setIsCorrecting(false);
              setFinalResults((prev) => ({ ...prev, writingFeedback: data.writingFeedback, writingData: data.writingData }));
              return;
            }
          })
          .catch((err) => console.warn('Re-fetch exam result for writingFeedback failed:', err));
      }
      // Show "Evaluating" whenever writing is still pending (even if auth not ready yet, e.g. after hard refresh)
      if (finalResults.writingSubmissionId && finalResults.id) {
        if (isMounted) setIsCorrecting(true);
      }
      // Background listener: subscribe once auth is available so we get live updates and can clear the mask
      if (finalResults.writingSubmissionId && finalResults.id && auth.currentUser) {
        const submissionRef = doc(db, 'writingSubmissions', finalResults.writingSubmissionId);
        unsubscribeWriting = onSnapshot(submissionRef, (snap) => {
          if (!snap.exists()) return;
          const data = snap.data();

          if (data.status === 'corrected') {
            if (!isMounted) return;
            setWritingFeedback(data);
            setIsCorrecting(false);

            const properWritingData = finalResults.writingData || (derived ?? { hasMultipleTexts: false, texts: {}, parts: [] });
            setWritingData(properWritingData);

            const aiWritingScore = data.scores?.overallScore ?? data.scores?.scaledScore ?? data.overallScore;
            let payload = { writingFeedback: data, writingData: properWritingData };

            if (aiWritingScore != null) {
              const provider = finalExamData.examProvider?.toUpperCase();
              const level = finalExamData.level?.toUpperCase();
              const writingScoreForCalc = provider === 'GOETHE'
                ? aiWritingScore
                : (Number(aiWritingScore) / (data.guidelineMetadata?.maxPoints || (level === 'C1' ? 48 : (provider === 'OSD' ? 30 : 45)))) * 100;
              const updatedScore = examService.calculateFinalScore(finalExamData, finalResults.userAnswers || {}, writingScoreForCalc, { writingFeedback: data });
              payload = { ...payload, ...updatedScore };
              setFinalResults((prev) => ({ ...prev, ...updatedScore, writingFeedback: data }));
            }
            enhancedUserService.updateExamResultWithFeedback(auth.currentUser.uid, finalResults.id, payload).catch((err) => console.warn('Fallback persist writing feedback failed:', err));
            if (unsubscribeWriting) unsubscribeWriting(); // Stop listening once corrected
          } else if (data.status?.startsWith('error_')) {
            if (isMounted) {
              setIsCorrecting(false);
              setCorrectionError(t('examResults.correctionFailed'));
            }
            if (unsubscribeWriting) unsubscribeWriting();
          }
        }, (err) => {
          console.warn('Fallback listen writing submission failed:', err);
          if (isMounted) setIsCorrecting(false);
        });
      }
    }
    setIsLoading(false);
  }
  return; // Always return for saved results
}

      // Only process new results if not viewing saved
      if (!isViewingSavedResult && finalExamData && finalResults && auth.currentUser) {
    if (finalResults.status === 'corrected') {
        if (isMounted) {
            setWritingFeedback(finalResults);
            setIsCorrecting(false);
            setIsLoading(false);
        }
        return;
    }

    const userId = auth.currentUser.uid;

    // Build writing texts using same logic as activityLogger/buildWritingDataFromExam (single source of truth; catches all key variants)
    const built = buildWritingDataFromExam(finalExamData, finalResults.userAnswers || {});
    const allWritingTexts = built?.texts || {};
    const writingPartsFound = built?.parts || [];

        // Ensure we have an exam result id when there is writing to submit (so cloud function can sync writingFeedback)
        let savedResultId = finalResults.id;
        const hasWritingToSubmit = Object.keys(allWritingTexts).length > 0;
        const hasValidScore = finalResults.provider === 'GOETHE'
          ? (finalResults.sectionScores && Object.keys(finalResults.sectionScores).length > 0)
          : (finalResults.overallScore > 0);

        // One save per attempt: if we're processing a different attempt (e.g. user opened another result), clear the promise ref so we create a new save. Prevents reusing an old doc id for a new attempt.
        //
        // overallScore is deliberately NOT part of this key. It used to be, and
        // because this page can recompute the score for the same sitting, the
        // key changed underneath a finished attempt, cleared the ref and wrote a
        // SECOND document. That is how one account ended up with the same
        // sitting stored 11 times with five different scores. examId + timeSpent
        // + completedAt identify the sitting; the score is an output of it.
        const attemptKey = `${finalExamData?.examId ?? ''}-${finalResults?.timeSpent ?? ''}-${finalResults?.completedAt ?? ''}`;
        if (lastSaveAttemptKeyRef.current !== attemptKey) {
          saveExamResultPromiseRef.current = null;
          lastSaveAttemptKeyRef.current = attemptKey;
        }

        if (!savedResultId && (hasValidScore || hasWritingToSubmit)) {
          if (!saveExamResultPromiseRef.current) {
            hasSavedRef.current = true;
            saveExamResultPromiseRef.current = (async () => {
              try {
                console.log(`Saving result to history (score: ${finalResults.overallScore}, hasWriting: ${hasWritingToSubmit}).`);
                const id = await logExamCompletion(finalExamData, finalResults);
                if (id) {
                  finalResults.id = id;
                  examResultIdRef.current = id;
                }
                return id;
              } catch (error) {
                console.warn("Failed to log exam completion:", error);
                return null;
              }
            })();
          }
          savedResultId = await saveExamResultPromiseRef.current;
        } else if (!savedResultId) {
          console.log(`Score is ${finalResults.overallScore}. Skipping save.`);
        }

        // Diagnostic: so we can see in console why examResultId might be missing on submission
        const effectiveResultId = savedResultId || examResultIdRef.current || finalResults?.id || null;
        if (hasWritingToSubmit && !effectiveResultId) {
          console.error('[ExamResults] Finish flow: have writing but no examResultId – save may have failed. Not submitting writing until result is saved.');
          if (isMounted) {
            setCorrectionError(t('examResults.correctionSubmitError'));
            setIsCorrecting(false);
          }
        }
        if (hasWritingToSubmit && effectiveResultId) {
          console.log('[ExamResults] Finish flow: examResultId for writing submission:', effectiveResultId, 'writingTextKeys:', Object.keys(allWritingTexts));
        }

        if (Object.keys(allWritingTexts).length > 0 && isMounted) {

           if (finalResults.writingFeedback) {
    console.log('⏭️ Skipping AI submission - feedback already exists');
    setWritingFeedback(finalResults.writingFeedback);
    setWritingData({
      hasMultipleTexts: Object.keys(allWritingTexts).length > 1,
      texts: allWritingTexts,
      parts: writingPartsFound
    });
    setIsCorrecting(false);
    setIsLoading(false);
    return; // Don't submit to AI
  }

  // Set the writing data with proper structure
  const properWritingData = {
    hasMultipleTexts: Object.keys(allWritingTexts).length > 1,
    texts: allWritingTexts,
    parts: writingPartsFound
  };

          setWritingData(properWritingData);
          setCorrectionError(null);

          const examResultIdForSubmission = savedResultId || examResultIdRef.current || finalResults?.id || null;
          if (!examResultIdForSubmission) {
            console.error('[ExamResults] Not submitting writing: no exam result id (save likely failed).');
            if (isMounted) {
              setIsCorrecting(false);
              setCorrectionError(t('examResults.correctionSubmitError'));
            }
          } else {
            setIsCorrecting(true);
          try {
            const submissionData = {
              examProvider: finalExamData.examProvider,
              examLevel: finalExamData.level,
              hasMultipleTexts: Object.keys(allWritingTexts).length > 1,
              texts: allWritingTexts,
              userText: Object.values(allWritingTexts)[0]?.text || '',
              taskType: 'writing',
              examResultId: examResultIdForSubmission
            };

            console.log('[ExamResults] Submitting writing for AI evaluation:', { examResultId: examResultIdForSubmission, textKeys: Object.keys(allWritingTexts) });
            const submissionId = await enhancedUserService.saveWritingSubmission(userId, submissionData);

            // Only treat string IDs as success (saveWritingSubmission returns null on invalid input)
            const hasValidSubmissionId = typeof submissionId === 'string' && submissionId.length > 0;
            if (!hasValidSubmissionId) {
              console.error('[ExamResults] saveWritingSubmission failed or returned invalid id:', submissionId);
              if (isMounted) {
                setCorrectionError(t('examResults.correctionSubmitError'));
                setIsCorrecting(false);
              }
            }

            // DO NOT block the database link with isMounted. If the user navigates away, we STILL need to link the IDs!
            if (hasValidSubmissionId) {
              const linkedResultId = examResultIdRef.current || finalResults.id || savedResultId;
              let unsubscribeWriting;

              if (linkedResultId) {
                console.log('Linking writing submission to exam result:', linkedResultId);
                // Fire and forget the DB update to guarantee it runs even if component is unmounting
                enhancedUserService.updateExamResultWithFeedback(
                  userId,
                  linkedResultId,
                  {
                    writingSubmissionId: submissionId,
                    writingData: properWritingData
                  }
                ).catch(err => console.error('Failed to link submission:', err));

                // Only update React state if still on the page
                if (isMounted) {
                  setFinalResults((prev) => ({ ...prev, writingSubmissionId: submissionId, id: prev.id || linkedResultId }));
                }
              }

              // Only attach live snapshot listeners if still on the page
              if (isMounted) {
                const submissionRef = doc(db, "writingSubmissions", submissionId);
                unsubscribeWriting = onSnapshot(submissionRef, async (docSnapshot) => {
                  if (docSnapshot.exists() && docSnapshot.data()?.status === 'corrected' && isMounted) {
                    const data = docSnapshot.data();
                    console.log('Received writing feedback:', data);

                    setWritingFeedback(data);
                    setIsCorrecting(false);

                    const resultIdForUpdate = examResultIdRef.current || linkedResultId || finalResults.id;
                    const aiWritingScore = data.scores?.overallScore ?? data.scores?.scaledScore ?? data.overallScore;
                    const hasWritingScore = aiWritingScore !== undefined && aiWritingScore !== null && resultIdForUpdate;

                    let payload = {
                      writingFeedback: data,
                      writingData: properWritingData
                    };

                    if (hasWritingScore) {
                      const provider = finalExamData.examProvider?.toUpperCase();
                      const level = finalExamData.level?.toUpperCase();
                      let writingScoreForCalc;

                      if (provider === 'GOETHE') {
                        writingScoreForCalc = aiWritingScore;
                      } else {
                        const maxWritingPoints = data.guidelineMetadata?.maxPoints || (level === 'C1' ? 48 : (provider === 'OSD' ? 30 : 45));
                        writingScoreForCalc = (Number(aiWritingScore) / maxWritingPoints) * 100;
                      }

                      const updatedScore = examService.calculateFinalScore(
                        finalExamData,
                        finalResults.userAnswers,
                        writingScoreForCalc,
                        { writingFeedback: data }
                      );
                      payload = { ...payload, ...updatedScore };
                      setFinalResults(prev => ({ ...prev, ...updatedScore, writingFeedback: data }));
                    }

                    if (resultIdForUpdate) {
                      await enhancedUserService.updateExamResultWithFeedback(userId, resultIdForUpdate, payload);
                    }
                    if (unsubscribeWriting) unsubscribeWriting();
                  }
                }, (error) => {
                  console.error("Error listening to submission:", error);
                  if (isMounted) {
                    setCorrectionError(t('examResults.correctionFetchError'));
                    setIsCorrecting(false);
                  }
                  if (unsubscribeWriting) unsubscribeWriting();
                });
              }
            }
          } catch (error) {
            if (isMounted) {
              setCorrectionError(t('examResults.correctionSubmitError'));
              setIsCorrecting(false);
            }
          }
          }
        }
      }

      if (isMounted) setIsLoading(false);
    };

    processResults();

    // Cleanup: do NOT clear saveExamResultPromiseRef so effect re-runs reuse the same save and do not create a second doc. unsubscribeWriting only.
    return () => {
      isMounted = false;
      hasProcessedResultsRef.current = false;
      if (unsubscribeWriting) unsubscribeWriting();
    };
  }, [finalExamData, finalResults, isViewingSavedResult, auth.currentUser?.uid]);

    useEffect(() => {
    if (activeTab === 'overview' && examSections.length > 0) {
      // Keep overview as default
    }
  }, [examSections, activeTab]);
  if (isLoading || !finalResults) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <LogamiLogoSpinner size="2xl" className="mx-auto mb-4" decorative={false} alt="Logami loading" />
          <p className="text-lg font-semibold text-gray-700">
            {isViewingSavedResult ? t('examResults.loadingSaved') : t('examResults.loading')}
          </p>
        </div>
      </div>
    );
  }

  const { overallScore: rawOverallScore = 0, sectionScores = {} } = finalResults;
const overallScore = rawOverallScore ?? 0; // Convert null to 0
const provider = finalResults?.provider || finalExamData?.examProvider;
const isGoethe = provider?.toUpperCase() === 'GOETHE';
const maxScore = finalResults.maxScore ?? 225;
const passingScorePct = finalExamData?.examFormat?.passingScore ?? 60; // percentage (e.g. 60 = 60%)
// For TELC/ÖSD: passingScore is a percentage; compare score as (points/max)*100 >= passingScore. Prefer stored passed when present.
const passed = isGoethe
  ? (finalResults.allModulesPassed ?? false)
  : (finalResults.passed !== undefined
      ? finalResults.passed
      : (maxScore > 0 ? (overallScore / maxScore) * 100 >= passingScorePct : false));


  const achievements = [
    { title: t('examResults.achievementExamComplete'), description: t('examResults.achievementExamCompleteDesc'), icon: Trophy, unlocked: true },
    { title: t('examResults.achievementPerfectionist'), description: t('examResults.achievementPerfectionistDesc'), icon: Target, unlocked: overallScore >= 90 },
    { title: t('examResults.achievementPersistent'), description: t('examResults.achievementPersistentDesc'), icon: TrendingUp, unlocked: Object.keys(sectionScores).length >= 3 }
  ];

  // Tagged paper: the corrected-exam view (Goethe shows its module verdicts there). Untagged
  // papers and any failure to build the insights fall through to the classic layout below.
  if (resultInsights) {
    const writingSection = examSections.find((s) => s.sectionId === 'schreiben' && s.hasContent);
    const hasWriting = !!(writingData && Object.keys(writingData.texts || {}).length > 0);
    return (
      <>
        <ResultsStory
          exam={finalExamData}
          results={finalResults}
          insights={resultInsights}
          taxonomy={resultTaxonomy}
          passed={passed}
          passPct={passingScorePct}
          isCorrecting={isCorrecting && hasWriting}
          writingSlot={writingSection && (hasWriting || isCorrecting || correctionError) ? (
            <SectionDetailContent
              sectionData={writingSection}
              results={finalResults}
              writingFeedback={writingFeedback}
              writingData={writingData}
              isCorrecting={isCorrecting}
              correctionError={correctionError}
              onRequestCorrection={handleRequestCorrection}
            />
          ) : null}
          writingCorrections={writingFeedback?.corrections || writingFeedback?.feedback?.corrections || []}
          writingScore={writingFeedback && !isCorrecting ? {
            // Same reading as the classic Schreiben card below.
            score: Number(writingFeedback.overallScore || writingFeedback.scores?.overallScore || 0),
            max: Number((sectionScores.schreiben || sectionScores.writing)?.maxScore || writingFeedback.guidelineMetadata?.maxPoints || (provider === 'OSD' ? 30 : 45)),
          } : null}
          formatAnswer={(value, question, part) => formatAnswerValue(value, question, part, t)}
          isFree={isFreeTierUser}
          onLocked={() => { setPendingExitNav(null); setShowAhaModal(true); }}
          onExit={() => handleExit('back')}
          previous={previousAttempt}
          retrySet={retrySet}
        />
        <AhaUpsellModal open={showAhaModal} onDismiss={handleAhaDismiss} onUpgrade={handleAhaUpgrade} context="exam" />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-[calc(4rem+max(0.75rem,env(safe-area-inset-bottom,0px)))] md:pb-0">
      {/* Header Section - compact on mobile */}
    <div className="relative overflow-hidden bg-white border-b border-gray-200 py-6 md:py-16">
  <div className="relative max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 text-center">
    <h1 className="font-space text-2xl sm:text-4xl md:text-5xl font-black text-black mb-1 md:mb-2 tracking-tight">
      {t('examResults.title')}
    </h1>
    <p className="text-sm sm:text-lg text-black/60 truncate max-w-[90vw] mx-auto">
      {finalExamData?.examName || examData?.examName || t('examResults.examNameFallback')} - {finalExamData?.level || examData?.level || 'B2'}
    </p>
  </div>
</div>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 -mt-6 md:-mt-16 relative z-10">
        <div className="space-y-4 md:space-y-8">
          {/* Overall Score Section - compact on mobile */}
<div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-8 hover:border-[#3A86FF]/30 transition-all duration-300">
  <div className="flex flex-col items-center">
    {isGoethe ? (
      isCorrecting ? (
        // MASKED GOETHE STATE
        <div className="text-center py-4">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-purple-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-purple-600 border-t-transparent animate-spin"></div>
            <PenTool className="w-6 h-6 text-purple-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-black mb-2">
            {t('examResults.goetheEvaluating', 'Ergebnisse werden ermittelt')}
          </h3>
          <p className="text-sm md:text-base text-black/60 max-w-sm mx-auto">
            {t('examResults.goetheEvaluatingDesc', 'Unser intelligentes Bewertungs-System analysiert Ihre schriftlichen Leistungen.')}
          </p>
        </div>
      ) : (
        (() => {
          const rawEntries = Object.entries(sectionScores || {}).filter(([, data]) => data?.passed);
          const seenCanonical = new Set();
          const passedEntries = rawEntries.filter(([id]) => {
            const canon = canonicalSectionId(id);
            if (seenCanonical.has(canon)) return false;
            seenCanonical.add(canon);
            return true;
          });
          const passedCount = passedEntries.length;
          const allCanonical = new Set(Object.keys(sectionScores || {}).map(canonicalSectionId));
          const totalCount = allCanonical.size;
          const passedNames = passedEntries
            .map(([id]) => getSectionDisplayName(id))
            .filter(Boolean);
          const passedList = passedNames.length ? passedNames.join(', ') : '';
          const allPassed = finalResults.allModulesPassed;
          return (
            <div className="text-center">
              <Trophy className={`w-14 h-14 md:w-24 md:h-24 mx-auto mb-2 md:mb-4 ${allPassed ? 'text-[#3A86FF]' : passedCount > 0 ? 'text-[#3A86FF]' : 'text-black/40'}`} />
              <h3 className="text-xl md:text-2xl font-bold text-black mb-1 md:mb-2">
                {allPassed ? t('examResults.passed') : passedCount > 0 ? t('examResults.modulesPassed', { count: passedCount }) : t('examResults.keepPracticing')}
              </h3>
              {(allPassed || passedCount > 0) && (
                <p className="text-sm md:text-base text-black/60">
                  {allPassed ? t('examResults.congratsAllPassed') : t('examResults.congratsPassedModules', { modules: passedList })}
                </p>
              )}
              {passedCount > 0 && (
                <div className="mt-3 md:mt-4 flex flex-wrap justify-center gap-2">
                  {passedNames.map((name) => (
                    <span key={name} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#3A86FF]/10 text-[#3A86FF] text-xs md:text-sm font-semibold">
                      <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                      {name}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-2 md:mt-3 text-xs md:text-sm text-black/50">
                {totalCount} {t('examResults.modulesChecked')}
              </div>
            </div>
          );
        })()
      )
    ) : (
      <AnimatedScoreCircle
  score={finalResults.overallScore || overallScore}
  maxScore={finalResults.maxScore || 225}
  passed={passed}
  showAsPoints={true}
  isCorrecting={isCorrecting}
  hasWriting={writingData && Object.keys(writingData.texts || {}).length > 0}
/>
    )}
  </div>
</div>

          {/* Tab Navigation - mobile: arrows + 2 tabs; desktop: all tabs */}
          <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 overflow-hidden">
  <div className="bg-white border-b border-gray-200">
    {/* Mobile: arrows + visible tabs (admin-dashboard style) */}
    <nav className="flex md:hidden items-center gap-1 px-1 py-2" aria-label="Tabs">
      {mobileTabOffset > 0 && (
        <button
          type="button"
          onClick={() => setMobileTabOffset((o) => Math.max(0, o - 1))}
          className="min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg border-0 bg-gray-100 text-gray-700 touch-manipulation flex-shrink-0"
          aria-label="Previous tabs"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      <div className="flex flex-1 justify-center gap-1 min-w-0">
        {visibleTabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              role="tab"
              aria-selected={isActive}
              className={`flex-1 min-w-0 px-2 py-3 rounded-lg font-semibold text-xs transition-all duration-200 touch-manipulation ${
                isActive
                  ? 'bg-[#3A86FF]/10 text-[#3A86FF] border border-[#3A86FF]/30'
                  : 'text-black/60 hover:bg-gray-50 border border-transparent'
              }`}
            >
              <div className="flex items-center justify-center gap-1.5">
                <tab.icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{tab.name}</span>
              </div>
            </button>
          );
        })}
      </div>
      {mobileTabOffset < maxMobileOffset && (
        <button
          type="button"
          onClick={() => setMobileTabOffset((o) => Math.min(maxMobileOffset, o + 1))}
          className="min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg border-0 bg-gray-100 text-gray-700 touch-manipulation flex-shrink-0"
          aria-label="Next tabs"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
    </nav>
    {/* Desktop: all tabs */}
    <nav className="hidden md:flex space-x-0" aria-label="Tabs">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`relative flex-1 px-6 py-4 font-semibold text-sm transition-all duration-300 ${
              isActive 
                ? 'text-black border-b-2 border-[#3A86FF]' 
                : 'text-black/60 hover:text-black hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <tab.icon className="w-5 h-5" />
              <span>{tab.name}</span>
            </div>
          </button>
        );
      })}
    </nav>
  </div>

            {/* Tab Content */}
            <div className="min-h-64 md:min-h-96">
              {activeTab === 'overview' ? (
                /* Overview Tab Content */
                <div className="p-4 md:p-8">
                   {/* Section Overview */}
                  <div>
  <h4 className="text-base md:text-lg font-bold text-gray-800 mb-3 md:mb-4">{t('examResults.sectionOverview')}</h4>
  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
    {examSections
      .filter(section => {
  const scoreData = sectionScores[section.originalSectionId] || sectionScores[section.sectionId];
  // Always show writing section, even if no score data yet
  const isWritingSection = section.sectionId === 'schreiben' || section.originalSectionId?.toLowerCase().includes('writing') || section.originalSectionId?.toLowerCase().includes('schreiben');
  // Never show speaking section
  const isSpeakingSection = section.sectionId === 'sprechen' || section.originalSectionId?.toLowerCase().includes('speaking') || section.originalSectionId?.toLowerCase().includes('sprechen');

  return (scoreData || isWritingSection) && !isSpeakingSection;
})
      .map((section) => {
  // Find the score data using either original or mapped section ID
  const scoreData = sectionScores[section.originalSectionId] || sectionScores[section.sectionId];

  // Check if this is a writing section
  const isWritingSection = section.sectionId === 'schreiben' || section.originalSectionId?.toLowerCase().includes('writing') || section.originalSectionId?.toLowerCase().includes('schreiben');

  // If writing section but no score data yet (AI processing)
  if (isWritingSection && !scoreData) {
    return (
      <div
        key={section.sectionId}
        onClick={() => setActiveTab(section.sectionId)}
        className="p-4 bg-white rounded-xl border border-gray-200 hover:border-[#3A86FF]/50 hover:bg-gray-50 cursor-pointer transition-all duration-300"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <PenTool className="w-5 h-5 text-black" />
            <span className="font-semibold text-black capitalize">{section.sectionName}</span>
          </div>
          <div className="text-right">
            {isCorrecting ? (
              <>
                <p className="text-sm font-semibold text-[#3A86FF] animate-pulse">
                  {t('examResults.aiGrading')}
                </p>
                <p className="text-xs text-black/60">
                  {t('examResults.pleaseWait')}
                </p>
              </>
            ) : writingFeedback ? (
  <>
    {/* --- MODIFIED BLOCK START --- */}
    <p className="text-lg font-bold text-black">
      {(() => {
 // 1. Get the score. Check for OSD (flat) structure first, then TELC (nested)
const score = writingFeedback.overallScore || writingFeedback.scores?.overallScore || 0;

// 2. Get the max score.
const writingScoreData = sectionScores['schreiben'] || sectionScores['writing'];
let maxScore = writingScoreData?.maxScore;

// If that's missing, try the guideline metadata or fallback
if (!maxScore) {
  // Use guideline metadata (which is 30 for ÖSD) or fallback
  maxScore = writingFeedback.guidelineMetadata?.maxPoints || (provider === 'OSD' ? 30 : 45);
}

return `${Math.round(score)}/${maxScore}`;
})()}
    </p>
    {/* --- MODIFIED BLOCK END --- */}
    <p className="text-sm text-black/60">
      {t('examResults.graded')}
    </p>
  </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-500">, </p>
                <p className="text-xs text-black/60">
                  {writingData && Object.keys(writingData.texts || {}).length > 0
                    ? t('examResults.writingNotYetEvaluated')
                    : t('examResults.noData')}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Regular sections with score data
  if (!scoreData) return null;

  return (
    <div
      key={section.sectionId}
      onClick={() => setActiveTab(section.sectionId)}
      className="p-4 bg-white rounded-xl border border-gray-200 hover:border-[#3A86FF]/50 hover:bg-gray-50 cursor-pointer transition-all duration-300"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {React.createElement(getSectionIcon(section.sectionId), { className: "w-5 h-5 text-black" })}
          <span className="font-semibold text-black capitalize">{section.sectionName}</span>
        </div>
        <div className="text-right">
          {finalResults.provider && (finalResults.provider === 'TELC' || finalResults.provider === 'OSD') ? (
            // TELC/ÖSD: Show points
            <>
              <p className="text-lg font-bold text-black">
                {Math.round(scoreData.score || 0)}/{scoreData.maxScore || 100}
              </p>
              <p className="text-sm text-black/60">
                {scoreData.percentage?.toFixed(0)}%
              </p>
            </>
          ) : (
            // Goethe: Show percentage and points (e.g. 40/100)
            <>
              <p className="text-lg font-bold text-black">{scoreData.percentage?.toFixed(0)}%</p>
              <p className="text-sm text-black/60">
                {Number(scoreData.score ?? 0).toLocaleString('de-DE', { maximumFractionDigits: 1 })}/{scoreData.maxScore ?? scoreData.total ?? 100}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
})
      }
  </div>
</div>



                  {/* Achievements */}
                  <div className="mb-4 md:mb-8">
                    <h4 className="text-base md:text-lg font-bold text-gray-800 mb-3 md:mb-4">{t('examResults.achievements')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                      {achievements.map((achievement) => (
                        <AchievementBadge key={achievement.title} {...achievement} />
                      ))}
                    </div>
                  </div>


                  </div>
              ) : activeTab === 'analytics' ? ( // <-- ADD THIS BLOCK
                /* Analytics Tab Content */
                <ExamAnalytics
                  currentExam={finalResults}
                  examData={finalExamData}
                />
              ) : (
                /* Section Detail Content */
                <SectionDetailContent
                  sectionData={examSections.find(s => s.sectionId === activeTab)}
                  results={finalResults}
                  writingFeedback={writingFeedback}
                  writingData={writingData}
                  isCorrecting={isCorrecting}
                  correctionError={correctionError}
                  onRequestCorrection={handleRequestCorrection}
                />
              )}
            </div>
          </div>
        </div>

        {/* Back Button - desktop only; mobile uses bottom nav */}
        <div className="mt-8 md:mt-12 mb-20 md:mb-8 text-center animate-fade-in hidden md:block" style={{ animationDelay: '2000ms' }}>
          <button
            onClick={() => handleExit('back')}
            className="inline-flex items-center px-8 py-4 rounded-xl font-semibold text-white bg-[#3A86FF] hover:bg-[#3A86FF]/90 transform hover:-translate-y-0.5 transition-all duration-200"
          >
            <ArrowLeft className="w-5 h-5 mr-3" />
            {t('examResults.backToDashboard')}
          </button>
        </div>
      </div>

      {/* Mobile bottom nav - same style as Exam Instructions / Practice */}
      <footer
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-[#E1E4E8]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <nav
          className="flex items-stretch justify-around h-16 px-2 max-w-lg mx-auto"
          aria-label="Ergebnis-Navigation"
        >
          <button
            type="button"
            onClick={() => handleExit('dashboard')}
            className="flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation active:scale-95"
            aria-label={t('nav.dashboard')}
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-xl hover:bg-gray-100">
              <Home className="w-[22px] h-[22px] text-[#6B7280]" strokeWidth={2} />
            </div>
            <span className="text-[10px] leading-none font-medium text-[#6B7280]">{t('nav.dashboard')}</span>
          </button>
          <button
            type="button"
            onClick={() => handleExit('exam')}
            className="flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation active:scale-95"
            aria-label="Prüfung"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-xl hover:bg-gray-100">
              <FileText className="w-[22px] h-[22px] text-[#6B7280]" strokeWidth={2} />
            </div>
            <span className="text-[10px] leading-none font-medium text-[#6B7280]">Prüfung</span>
          </button>
          <button
            type="button"
            onClick={() => handleExit('lessons')}
            className="flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation active:scale-95"
            aria-label="Lektionen"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-xl hover:bg-gray-100">
              <Book className="w-[22px] h-[22px] text-[#6B7280]" strokeWidth={2} />
            </div>
            <span className="text-[10px] leading-none font-medium text-[#6B7280]">Lektionen</span>
          </button>
        </nav>
      </footer>


      {/* Aha upsell modal */}
      <AhaUpsellModal
        open={showAhaModal}
        onDismiss={handleAhaDismiss}
        onUpgrade={handleAhaUpgrade}
        context="exam"
      />
    </div>
  );
};

export default ExamResultsPage;